import { useState } from "react";
import type { CraftableWeapon, MaterialDefinition } from "@/lib/crafting/types";
import { SOCKET_LABELS, SOCKET_ORDER } from "@/lib/crafting/types";
import type { Planner } from "@/hooks/usePlanner";
import { findComponent } from "@/lib/crafting/engine";
import { buildSharedPlan, planToUrl, savePlan } from "@/lib/crafting/plan";
import { createSharedPlan } from "@/lib/manifest/client";
import { ResourceLine } from "./ResourceLine";
import { sortResources } from "./PlanSummaryPanel";
import { assetUrl } from "@/lib/bungie/assets";

export interface CraftingReviewScreenProps {
  weapon: CraftableWeapon;
  planner: Planner;
  materials: Record<string, MaterialDefinition>;
  onBack: () => void;
  session?: { signedIn: boolean; displayName?: string | null };
  /** Set when planning against a weapon you own; saved locally so reopening the
   *  plan can show the weapon's current level. Never leaves this browser. */
  ownedInstanceId?: string;
}

/**
 * The final review state.
 *
 * Deliberately has no "Reshape Weapon" button — this application never modifies
 * a real item. It is a summary you take with you into the game.
 */
export function CraftingReviewScreen({
  weapon,
  planner,
  materials,
  onBack,
  session,
  ownedInstanceId,
}: CraftingReviewScreenProps) {
  const { state, derived } = planner;
  const { progression, cost, blame } = derived;
  const shaping = state.mode === "new-shape";
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareNote, setShareNote] = useState<string | null>(null);
  const [includeProgress, setIncludeProgress] = useState(false);

  function currentPlan() {
    return buildSharedPlan({
      recipeHash: weapon.recipeHash,
      mode: state.mode,
      plannedConfiguration: state.plannedConfiguration,
      baseConfiguration: state.currentConfiguration,
      ...(includeProgress
        ? {
            currentWeaponLevel: state.currentWeaponLevel,
            currentLevelPercent: state.currentLevelPercent,
          }
        : {}),
    });
  }

  async function writeToClipboard(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard can be blocked; show the URL so the user can copy it manually.
      window.prompt("Copy this plan URL:", url);
    }
  }

  /**
   * Signed in, the plan is stored server-side so the link can be credited to
   * you. Signed out — or if storing fails — it falls back to the stateless link,
   * which works exactly as well, just anonymously.
   */
  async function copyLink() {
    const plan = currentPlan();
    const fallback = planToUrl(plan, window.location.origin);

    if (!session?.signedIn) {
      setShareNote("Anonymous link. Sign in with Bungie to put your name on it.");
      await writeToClipboard(fallback);
      return;
    }

    setSharing(true);
    const result = await createSharedPlan(plan);
    setSharing(false);

    if (result.status === "ok") {
      setShareNote(
        session.displayName
          ? `Shared as ${session.displayName}.`
          : "Shared with your Bungie name."
      );
      await writeToClipboard(result.url);
      return;
    }

    setShareNote(
      result.status === "unauthenticated"
        ? "Your sign-in expired, so this link is anonymous."
        : `${result.message} Copied an anonymous link instead.`
    );
    await writeToClipboard(fallback);
  }

  function handleSave() {
    savePlan(window.localStorage, currentPlan(), `${weapon.name} plan`, ownedInstanceId);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  const icon = assetUrl(weapon.icon);

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-8 sm:py-12">
      <button
        type="button"
        onClick={onBack}
        className="label-caps mb-8 text-xs text-ink-2 transition-colors hover:text-ink"
      >
        ← Back to crafting
      </button>

      <header className="corner-brackets flex items-start gap-5 border border-line-dim bg-black/40 p-5">
        {icon && <img src={icon} alt="" aria-hidden className="h-16 w-16" />}
        <div>
          <p className="type-tracked text-[0.65rem] text-ink-2">Review plan</p>
          <h1 className="type-display mt-1 text-3xl leading-none sm:text-4xl">
            {weapon.name}
          </h1>
          <p className="label-caps mt-2 text-sm text-ink-2">
            {shaping ? "New shape" : "Reshape of an owned weapon"}
          </p>
        </div>
      </header>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        {/* --- target ------------------------------------------------------- */}
        <Section title="Weapon target">
          <Row label="Current weapon level" value={String(state.currentWeaponLevel)} />
          <Row label="Current progress" value={`${state.currentLevelPercent}%`} />
          <Row
            label="Required weapon level"
            value={String(progression.requiredWeaponLevel)}
            emphasis
          />
          {blame && (
            <Row
              label="Highest requirement"
              value={`${blame.component.name} (${SOCKET_LABELS[blame.key]}) — Lv. ${blame.component.requiredLevel}`}
            />
          )}
        </Section>

        {/* --- progression --------------------------------------------------- */}
        <Section title="Progression">
          <Row
            label="Remaining levels"
            value={
              progression.remainingLevels === 0
                ? "None"
                : progression.remainingLevels.toFixed(2)
            }
            emphasis
          />
          {progression.estimatedKills !== undefined && (
            <Row
              label="Estimated enemy defeats"
              value={`~${progression.estimatedKills.toLocaleString()}`}
              hint="estimate"
            />
          )}
          {progression.estimatedActivities.map((a) => (
            <Row
              key={a.label}
              label={a.label}
              value={`~${a.completions} completions`}
              hint="estimate"
            />
          ))}
          <p
            className={`mt-3 text-sm ${progression.alreadyAvailable ? "text-ok" : "text-level"}`}
          >
            {progression.alreadyAvailable
              ? "✓ Every selected component is currently unlocked."
              : "Some selected components are still locked at your current level."}
          </p>
        </Section>

        {/* --- configuration comparison --------------------------------------- */}
        <Section title="Configuration" className="lg:col-span-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="label-caps text-xs text-ink-3">
                <th scope="col" className="py-2 text-left font-normal">
                  Slot
                </th>
                <th scope="col" className="py-2 text-left font-normal">
                  {shaping ? "Shape now" : "Current"}
                </th>
                <th scope="col" className="py-2 text-left font-normal">
                  Planned
                </th>
                <th scope="col" className="py-2 text-right font-normal">
                  Level
                </th>
              </tr>
            </thead>
            <tbody>
              {SOCKET_ORDER.filter((key) => weapon.sockets.some((s) => s.key === key)).map((key) => {
                // Compare against the same base the cost engine used: the real
                // weapon when reshaping, or what is buildable now when shaping.
                const current = findComponent(weapon, key, derived.base?.[key]);
                const planned = findComponent(weapon, key, state.plannedConfiguration[key]);
                const changed = current?.plugHash !== planned?.plugHash;
                return (
                  <tr key={key} className="border-t border-line-dim">
                    <th scope="row" className="py-2 text-left font-normal text-ink-2">
                      {SOCKET_LABELS[key]}
                    </th>
                    <td className="py-2 text-ink-3">{current?.name ?? "—"}</td>
                    <td className="py-2">
                      {planned ? (
                        <span className={changed ? "font-semibold text-enhanced" : undefined}>
                          {planned.name}
                          {planned.enhanced && (
                            <span className="ml-1 text-xs text-enhanced">↑ Enhanced</span>
                          )}
                          {changed && <span className="sr-only"> (changed)</span>}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {planned ? planned.requiredLevel : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Section>

        {/* --- resources ----------------------------------------------------- */}
        {shaping && cost.shapeNow.length > 0 && (
          <Section title="Shape-now resources">
            <ResourceList resources={cost.shapeNow} materials={materials} />
          </Section>
        )}
        {cost.reshapeLater.length > 0 && (
          <Section title={shaping ? "Reshape resources" : "Reshape resources"}>
            <ResourceList resources={cost.reshapeLater} materials={materials} />
            <p className="mt-3 text-xs text-ink-3">
              Unchanged components are not charged.
            </p>
          </Section>
        )}
        {cost.total.length > 0 && (
          <Section title="Total investment" className="lg:col-span-2">
            <ResourceList resources={cost.total} materials={materials} />
            <p className="mt-3 text-xs text-ink-3">
              <span aria-hidden className="mr-1">ⓘ</span>
              Your resource inventory is not connected. Sign in with Bungie to compare these
              against what you actually hold.
            </p>
          </Section>
        )}
        {cost.total.length === 0 && (
          <Section title="Total investment" className="lg:col-span-2">
            <p className="text-sm text-ink-3">
              This plan costs nothing — it matches the current configuration.
            </p>
          </Section>
        )}
      </div>

      {/* --- actions -------------------------------------------------------- */}
      <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-line-dim pt-6">
        <button
          type="button"
          onClick={handleSave}
          className="type-button border border-line bg-white/10 px-4 py-2.5 text-xs transition-colors hover:bg-white/20"
        >
          {saved ? "Saved ✓" : "Save Reshape Plan"}
        </button>
        <button
          type="button"
          onClick={copyLink}
          disabled={sharing}
          className="type-button border border-line-dim px-4 py-2.5 text-xs transition-colors hover:bg-white/10 disabled:opacity-50"
        >
          {sharing ? "Sharing…" : copied ? "Copied ✓" : "Copy Plan"}
        </button>
        <label className="ml-2 flex items-center gap-2 text-sm text-ink-2">
          <input
            type="checkbox"
            checked={includeProgress}
            onChange={(e) => setIncludeProgress(e.target.checked)}
            className="h-4 w-4"
          />
          Include my level and progress in the shared link
        </label>
      </div>

      {shareNote && (
        <p role="status" className="mt-3 text-xs text-ink-2">
          {shareNote}
        </p>
      )}

      <p className="mt-6 text-xs text-ink-3">
        This is a planning tool. It never crafts, reshapes, or modifies anything on your Bungie
        account.
      </p>
    </div>
  );
}

function Section({
  title,
  children,
  className = "",
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`corner-brackets border border-line-dim bg-black/40 p-5 ${className}`}
    >
      <h2 className="label-caps mb-3 text-sm text-ink-2">{title}</h2>
      {children}
    </section>
  );
}

function Row({
  label,
  value,
  emphasis,
  hint,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
  hint?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1 text-sm">
      <span className="text-ink-2">
        {label}
        {hint && <span className="ml-1.5 text-xs text-ink-3">({hint})</span>}
      </span>
      <span className={emphasis ? "text-lg font-semibold tabular-nums" : "tabular-nums"}>
        {value}
      </span>
    </div>
  );
}

function ResourceList({
  resources,
  materials,
}: {
  resources: { itemHash: number; quantity: number }[];
  materials: Record<string, MaterialDefinition>;
}) {
  return (
    <ul className="space-y-1.5">
      {sortResources(resources).map((r) => (
        <li key={r.itemHash}>
          <ResourceLine amount={r} materials={materials} />
        </li>
      ))}
    </ul>
  );
}

